import { memo, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { HStack, Text, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { SelectCharacteristic } from './components/SelectCharacteristic';
import {
    type VariantInfo,
    type VariantInfoDisplayType,
    VariantInfoDisplayTypes,
    VariantInfoValueStatuses,
} from '../../api/variants';
import { ChipRadio, ColorDotRadio, PreviewRadio, RadioGroup } from '../RadioGroup';
import { SizeTableButton } from '../SizeTable';
import styles from './CharacteristicsList.module.css';

const MAX_VISIBLE_ITEMS_MAPPING: Record<VariantInfoDisplayType, number> = {
    COLOR: 20,
    IMAGE: 10,
    TEXT: 12,
    SELECT: 12,
};

const MAX_VISIBLE_ITEMS_DEFAULT = 8;

interface CharacteristicItemProps extends TestProps {
    characteristic: VariantInfo;
    className?: string;
    sizeTableSrc?: string;
    isCollapsable?: boolean;
    isWrappable?: boolean;
    onChange: (newValue: string) => void;
    onSizeTableOpen: VoidFunction;
}

const CharacteristicItem = memo(function CharacteristicItem({
    'data-testid': dataTestId = 'CharacteristicItem',
    characteristic,
    className,
    sizeTableSrc,
    isCollapsable,
    isWrappable = false,
    onChange,
    onSizeTableOpen,
}: CharacteristicItemProps) {
    const { title, values, display_type: displayType, selected_value: selectedValue } = characteristic;
    const isSizeCharacteristic = title === 'Размер';

    const maxVisibleItems = useMemo(() => {
        if (displayType) {
            return MAX_VISIBLE_ITEMS_MAPPING[displayType];
        }

        return MAX_VISIBLE_ITEMS_DEFAULT;
    }, [displayType]);

    const renderRadioOptions = useCallback(() => {
        if (!values || values.length === 0) {
            return null;
        }

        return values.map((value) => {
            const isDashed = value.status === VariantInfoValueStatuses.PARTIALLY_AVAILABLE;
            const isDisabled = value.status === VariantInfoValueStatuses.UNAVAILABLE;

            switch (displayType) {
                case VariantInfoDisplayTypes.COLOR:
                    return (
                        <ColorDotRadio
                            key={value.value}
                            value={value.value}
                            color={value.color}
                            isDashed={isDashed}
                            isDisabled={isDisabled}
                        />
                    );
                case VariantInfoDisplayTypes.IMAGE:
                    return (
                        <PreviewRadio
                            key={value.value}
                            value={value.value}
                            isDashed={isDashed}
                            image={value.image}
                            isDisabled={isDisabled}
                        />
                    );
                case VariantInfoDisplayTypes.TEXT:
                    return (
                        <ChipRadio key={value.value} value={value.value} isDashed={isDashed} isDisabled={isDisabled}>
                            {value.value}
                        </ChipRadio>
                    );

                default:
                    return null;
            }
        });
    }, [values, displayType]);

    const isShowValue = Boolean(displayType && displayType !== VariantInfoDisplayTypes.SELECT && selectedValue);

    const characteristicHeader = (
        <HStack gap="8" className={styles.header} justifyContent="space-between" alignItems="center">
            <HStack className={styles.title}>
                <Text size="mc" role="supplementary">
                    {title}
                    {isShowValue && ':'}
                    &nbsp;
                </Text>

                {isShowValue && <Text size="mc">{selectedValue}</Text>}
            </HStack>
            {isSizeCharacteristic && sizeTableSrc && <SizeTableButton openModal={onSizeTableOpen} />}
        </HStack>
    );

    if (!displayType || displayType === VariantInfoDisplayTypes.SELECT) {
        return (
            <VStack gap="12">
                {characteristicHeader}
                <SelectCharacteristic characteristic={characteristic} onChange={onChange} />
            </VStack>
        );
    }

    return (
        <VStack data-testid={`${dataTestId}.CharacteristicItem`} gap="12">
            {characteristicHeader}
            <RadioGroup
                data-testid={`${dataTestId}.RadioGroup`}
                value={selectedValue}
                onChange={onChange}
                aria-label={`Выбор ${title}`}
                className={cn(styles.radioGroup, className)}
                maxVisibleItems={maxVisibleItems}
                disableCollapse={!isCollapsable}
                isWrappable={isWrappable}
                displayType={displayType}
            >
                {renderRadioOptions()}
            </RadioGroup>
        </VStack>
    );
});

interface CharacteristicsListProps extends TestProps {
    sizeTableSrc?: string;
    characteristics: Array<VariantInfo>;
    isCollapsable?: boolean;
    isWrappable?: boolean;
    onCharacteristicsChange?: (changedCharacteristic: VariantInfo) => void;
    onSizeTableOpen: VoidFunction;
}

export const CharacteristicsList = memo(function CharacteristicsList({
    'data-testid': dataTestId = 'CharacteristicsList',
    sizeTableSrc,
    characteristics,
    isCollapsable,
    isWrappable = false,
    onCharacteristicsChange,
    onSizeTableOpen,
}: CharacteristicsListProps) {
    const handleCharacteristicChange = useCallback(
        (characteristicId: string, newValue: string) => {
            const characteristicToChange = characteristics.find((c) => c.id === characteristicId);

            if (characteristicToChange && characteristicToChange.selected_value !== newValue) {
                onCharacteristicsChange?.({ ...characteristicToChange, selected_value: newValue });
            }
        },
        [characteristics, onCharacteristicsChange]
    );

    return (
        <VStack data-testid={`${dataTestId}`} gap="20">
            {characteristics.map((characteristic) => (
                <CharacteristicItem
                    key={characteristic.id}
                    data-testid={dataTestId}
                    className={cn({ [styles.scrollable]: !isWrappable })}
                    characteristic={characteristic}
                    sizeTableSrc={sizeTableSrc}
                    isCollapsable={isCollapsable}
                    isWrappable={isWrappable}
                    onChange={(newValue) => handleCharacteristicChange(characteristic.id, newValue)}
                    onSizeTableOpen={onSizeTableOpen}
                />
            ))}
        </VStack>
    );
});
